import { useState, useEffect } from "react";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { FileText, Loader2, RefreshCw, CheckCircle, Clock, AlertCircle, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import EmptyState from "@/components/EmptyState";
import PageHeader from "@/components/PageHeader";
import IntakeQReconnectDialog from "@/components/IntakeQReconnectDialog";

const statusConfig = {
  completed: { icon: CheckCircle, color: "text-green-600 bg-green-50 border-green-200", label: "Completed" },
  pending: { icon: Clock, color: "text-amber-600 bg-amber-50 border-amber-200", label: "Pending" },
  sent: { icon: Clock, color: "text-blue-600 bg-blue-50 border-blue-200", label: "Sent" },
  opened: { icon: Clock, color: "text-blue-600 bg-blue-50 border-blue-200", label: "In Progress" },
};

export default function Forms() {
  const { user } = useAuth();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("local");
  const [syncing, setSyncing] = useState(false);
  const [reconnectOpen, setReconnectOpen] = useState(false);

  const fetchForms = async (showSync = false) => {
    if (showSync) setSyncing(true);
    try {
      const { data: intakeqData } = await API.get("/intakeq/forms");
      if (intakeqData?.forms?.length > 0) {
        setForms(intakeqData.forms);
        setSource("intakeq");
      } else {
        const { data: localData } = await API.get("/patient-forms");
        setForms(localData || []);
        setSource("local");
      }
    } catch {
      try {
        const { data: localData } = await API.get("/patient-forms");
        setForms(localData || []);
        setSource("local");
      } catch { }
    }
    setLoading(false);
    setSyncing(false);
  };

  useEffect(() => { if (user) fetchForms(); }, [user]);

  const completed = forms.filter(f => f.status?.toLowerCase() === "completed");
  const pending = forms.filter(f => f.status?.toLowerCase() !== "completed");

  if (loading) {
    return (
      <div className="max-w-lg mx-auto px-4 py-6 flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
          <p className="text-sm text-muted-foreground">Loading forms...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-6 animate-fade-in" data-testid="forms-page">
      <div className="flex items-start justify-between">
        <PageHeader title="Forms" subtitle="Your intake forms and questionnaires" />
        <Button variant="ghost" size="sm" onClick={() => fetchForms(true)} disabled={syncing} className="gap-1.5 mt-2" data-testid="sync-forms-button">
          <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Syncing..." : "Sync"}
        </Button>
      </div>

      {source === "intakeq" && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 mb-4 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
          <p className="text-xs text-green-800 font-medium">Synced from IntakeQ</p>
        </div>
      )}

      <Tabs defaultValue="pending" className="w-full">
        <TabsList className="w-full mb-4">
          <TabsTrigger value="pending" className="flex-1" data-testid="tab-pending">Pending ({pending.length})</TabsTrigger>
          <TabsTrigger value="completed" className="flex-1" data-testid="tab-completed">Completed ({completed.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="space-y-3">
          {pending.length === 0 ? (
            <div className="space-y-3">
              <EmptyState icon={FileText} title="No pending forms" description="All your forms are up to date!" />
              {source === "local" && forms.length === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-amber-800 mb-2">Not seeing your forms? Try connecting your IntakeQ account.</p>
                    <button onClick={() => setReconnectOpen(true)} className="text-xs font-medium text-amber-900 underline" data-testid="reconnect-forms-button">
                      Reconnect IntakeQ
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            pending.map((form) => <FormCard key={form.id || form.intakeq_form_id} form={form} />)
          )}
        </TabsContent>
        <TabsContent value="completed" className="space-y-3">
          {completed.length === 0 ? (
            <EmptyState icon={FileText} title="No completed forms" description="Completed forms will appear here" />
          ) : (
            completed.map((form) => <FormCard key={form.id || form.intakeq_form_id} form={form} />)
          )}
        </TabsContent>
      </Tabs>
      <IntakeQReconnectDialog open={reconnectOpen} onOpenChange={setReconnectOpen} onSuccess={() => fetchForms(true)} />
    </div>
  );
}

function FormCard({ form }) {
  const status = form.status?.toLowerCase() || "pending";
  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <div className="bg-card rounded-xl border border-border p-4 animate-fade-in" data-testid="form-card">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm">{form.title}</h3>
          {form.practitioner_name && <p className="text-xs text-muted-foreground mt-0.5">{form.practitioner_name}</p>}
        </div>
        <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border shrink-0 ${config.color}`}>
          <Icon className="w-3 h-3" />{config.label}
        </span>
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        {form.date_created && <span>Created: {form.date_created}</span>}
        {form.date_submitted && <span>Submitted: {form.date_submitted}</span>}
        {form.source === "intakeq" && <span className="text-green-600 font-medium">IntakeQ</span>}
      </div>
      {status !== "completed" && form.source === "intakeq" && (
        <a href="https://intakeq.com" target="_blank" rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
          Complete on IntakeQ <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  );
}
